/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience priority lists", description = "Control which audience takes precedence when users qualify for multiple campaigns or journeys, so each person receives only the highest-priority message.", lastUpdatedDate = new Date(1788480000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience priority lists",
        description: "Control which audience takes precedence when users qualify for multiple campaigns or journeys, so each person receives only the highest-priority message.",
        lastUpdatedDate: "Fri Sep 04 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "When to use a priority list",
          "depth": 1,
          "id": "when-to-use-a-priority-list"
        }, {
          "value": "Create a priority list",
          "depth": 0,
          "id": "create-a-priority-list"
        }, {
          "value": "Constraints",
          "depth": 1,
          "id": "constraints"
        }, {
          "value": "How priority lists affect other features",
          "depth": 0,
          "id": "how-priority-lists-affect-other-features"
        }, {
          "value": "Journeys",
          "depth": 1,
          "id": "journeys"
        }, {
          "value": "Insights",
          "depth": 1,
          "id": "insights"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      h3: "h3",
      hr: "hr",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/usage",
                children: "At least two audiences"
              }), " built using the same ", _jsx(_components.a, {
                href: "/customer-studio/schema#create-the-parent-model",
                children: "parent model"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Priority lists control which audience takes precedence when users qualify for more than one. When audiences overlap, Hightouch syncs each user only to their highest-priority audience — preventing duplicate messages and ensuring each person receives the most relevant campaign."
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Users often qualify for multiple audiences based on buying behavior, demographics, or lifecycle stage. If you run campaigns targeting each of those audiences, the same person can receive multiple messages — a winback email, a loyalty offer, and a re-engagement ad all in the same week."
      }), "\n", _jsx(_components.p, {
        children: "Priority lists solve this by assigning a rank to each audience. When a user qualifies for more than one audience in a priority list, Hightouch syncs them only to the highest-ranked audience. Priority is evaluated at sync time, so as users move in and out of audiences, their priority assignment updates automatically."
      }), "\n", _jsx(_components.h3, {
        id: "when-to-use-a-priority-list",
        children: "When to use a priority list"
      }), "\n", _jsx(_components.p, {
        children: "Use a priority list when you have audiences that overlap and you want to guarantee each user receives only one campaign. This is common for lifecycle campaigns where a user might qualify for multiple stages simultaneously (for example, both a \"trial expiring\" and \"hasn't activated\" audience)."
      }), "\n", _jsxs(_components.p, {
        children: ["If you just need to remove specific users from an audience — like excluding recent purchasers from a prospecting campaign — use ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audience exclusions"
        }), " instead. Exclusions remove users from a single audience; priority lists rank users across multiple audiences."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-priority-list",
        children: "Create a priority list"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsxs(_components.strong, {
              children: ["Customer Studio > ", _jsx(_components.a, {
                href: "https://app.hightouch.com/priority-lists?from=docs",
                children: "Priority lists"
              })]
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Add priority list"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/cs-pl-add-list.png",
              alt: "Add priority list"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Enter a ", _jsx(_components.strong, {
              children: "Name"
            }), " and select a ", _jsx(_components.strong, {
              children: "Parent model"
            }), ".\nAll audiences in the priority list must share this parent model."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/cs-pl-configure-list.png",
              alt: "Configure priority list"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Add audiences"
            }), " to open the audience picker."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/cs-pl-audience-modal.png",
              alt: "Add audience modal"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Drag audiences into the desired priority order. The audience at the top has the highest priority."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "hightouch-audiences/priority-lists/cs-pl-drag-drop.png",
              alt: "Drag and drop editor"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click ", _jsx(_components.strong, {
              children: "Save changes"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can return to this page anytime to reorder audiences or add new ones."
      }), "\n", _jsx(_components.h3, {
        id: "constraints",
        children: "Constraints"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Each audience can belong to only one priority list. If you need an audience in a second list, create a duplicate audience."
        }), "\n", _jsx(_components.li, {
          children: "All audiences in a priority list must use the same parent model."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-priority-lists-affect-other-features",
        children: "How priority lists affect other features"
      }), "\n", _jsx(_components.h3, {
        id: "journeys",
        children: "Journeys"
      }), "\n", _jsxs(_components.p, {
        children: ["If a ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "journey's"
        }), " entry audience belongs to a priority list, the priority is respected when determining who enters the journey. For example, if Audience B is ranked below Audience A and a user qualifies for both, the user enters only Audience A's journey. They won't enter Audience B's journey until they drop out of Audience A while still qualifying for Audience B. See ", _jsx(_components.a, {
          href: "/customer-studio/data-model#coordinate-lifecycle-state-across-campaigns",
          children: "coordinating overlapping journeys"
        }), " for details."]
      }), "\n", _jsx(_components.h3, {
        id: "insights",
        children: "Insights"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/insights",
          children: "Audience insights"
        }), " — including breakdowns and overlap analysis — take priority list membership into account. When you view overlap between two audiences that share a priority list, the numbers reflect the post-priority assignment, not raw qualification."]
      })]
    });
  }
}
export default MDXContent;
